import * as yup from "yup";
import { CheckoutPaymentMethod } from "./PaymentMethod.ts";
import { BusinessType } from "../MoneyTransfer/BusinessType.tsx";
import { ValidationError } from "yup";

export const validationSchema = yup
  .object({
    customer: yup.object({
      email: yup.string().email("Email non valida").required("Email richiesta"),
      firstName: yup
        .string()
        .min(2, "Nome troppo corto")
        .required("Nome richiesto"),
      lastName: yup
        .string()
        .min(2, "Cognome troppo corto")
        .required("Cognome richiesto"),
    }),
    dialCode: yup.string().optional(),
    countryCode: yup.string().optional(),
    number: yup.string().optional(),
    invoiceRequired: yup.boolean().default(false),
    businessType: yup.string().when(["invoiceRequired", "paymentMethod"], {
      is: (invoiceRequired: boolean) => invoiceRequired,
      then: (schema) =>
        schema
          .test(
            "valid-business-type-for-payment",
            "Tipo entità non valido per il metodo di pagamento selezionato",
            function (value) {
              const { paymentMethod } = this.parent;
              if (paymentMethod === CheckoutPaymentMethod.HeyLight) {
                return (
                  value === BusinessType.Person ||
                  value === BusinessType.IndividualCompany
                );
              }
              return [
                BusinessType.Person,
                BusinessType.IndividualCompany,
                BusinessType.Company,
              ].includes(value as BusinessType);
            },
          )
          .required("Tipo entità richiesto"),
      otherwise: (schema) => schema.notRequired(),
    }),
    businessName: yup
      .string()
      .when(["invoiceRequired", "businessType", "paymentMethod"], {
        is: (
          invoiceRequired: boolean,
          businessType: string,
          paymentMethod: string,
        ) =>
          invoiceRequired &&
          businessType === BusinessType.Company &&
          paymentMethod !== CheckoutPaymentMethod.HeyLight,
        then: (schema) =>
          schema.required("Ragione sociale richiesta per le Aziende"),
        otherwise: (schema) => schema.notRequired(),
      }),
    vat: yup.string().when(["invoiceRequired", "businessType"], {
      is: (invoiceRequired: boolean, businessType: string) =>
        invoiceRequired && businessType === BusinessType.IndividualCompany,
      then: (schema) => schema.required("Partita IVA richiesta"),
      otherwise: (schema) => schema.notRequired(),
    }),
    taxCode: yup
      .string()
      .when(["invoiceRequired", "businessType", "paymentMethod"], {
        is: (
          invoiceRequired: boolean,
          businessType: string,
          paymentMethod: string,
        ) =>
          invoiceRequired &&
          businessType &&
          paymentMethod !== CheckoutPaymentMethod.HeyLight,
        then: (schema) => schema.required("Codice fiscale richiesto"),
        otherwise: (schema) => schema.notRequired(),
      }),
    address: yup
      .object()
      .optional()
      .when(["invoiceRequired", "businessType", "paymentMethod"], {
        is: (
          invoiceRequired: boolean,
          businessType: string,
          paymentMethod: string,
        ) =>
          invoiceRequired &&
          businessType &&
          paymentMethod !== CheckoutPaymentMethod.HeyLight,
        then: (schema) =>
          schema.shape({
            street: yup.string().required("Indirizzo richiesto"),
            cap: yup
              .string()
              .required("Codice postale richiesto")
              .matches(/^\d{5}$/, "Codice postale non valido"),
            city: yup.string().required("Città richiesta"),
            province: yup.string().required("Provincia richiesta"),
          }),
        otherwise: (schema) =>
          schema.shape({
            street: yup.string().optional(),
            cap: yup.string().optional(),
            city: yup.string().optional(),
            province: yup.string().optional(),
          }),
      }),
    pec: yup.string().email().optional(),
    sdiCode: yup.string().optional(),
    privacyPolicyAccepted: yup
      .boolean()
      .required("Devi accettare la privacy policy")
      .oneOf([true], "Devi accettare la privacy policy"),
    additionalTerms: yup
      .boolean()
      .required("Devi accettare le clausole aggiuntive")
      .oneOf([true], "Devi accettare le clausole aggiuntive"),
    participants: yup
      .array()
      .of(
        yup.object({
          firstName: yup
            .string()
            .min(2, "Nome troppo corto")
            .required("Nome richiesto"),
          lastName: yup
            .string()
            .min(2, "Cognome troppo corto")
            .required("Cognome richiesto"),
          email: yup
            .string()
            .email("Email non valida")
            .required("Email richiesta"),
        }),
      )
      .default([]),
    paymentMethod: yup
      .string()
      .oneOf(Object.values(CheckoutPaymentMethod))
      .required("Seleziona un metodo di pagamento"),
    numberOfInstallments: yup.number().when("paymentMethod", {
      is: CheckoutPaymentMethod.StripeInstallments,
      then: (schema) =>
        schema
          .required("Seleziona il numero di rate")
          .min(1, "Seleziona il numero di rate"),
      otherwise: (schema) => schema.notRequired(),
    }),
    bundleParticipants: yup
      .array()
      .of(
        yup.object({
          products: yup
            .array()
            .of(
              yup
                .object({
                  id: yup.string().required(),
                  priceInCents: yup.object({
                    amount: yup.number().required(),
                    currency: yup.string().required(),
                  }),
                })
                .required(),
            )
            .required()
            .default([]),
          customer: yup
            .object({
              firstName: yup.string().required("Nome richiesto"),
              lastName: yup.string().required("Cognome richiesto"),
              email: yup
                .string()
                .email("Email non valida")
                .required("Email richiesta"),
            })
            .default({}),
        }),
      )
      .default([]),
  })
  .test("phone-required-for-heylight", "", function (value) {
    if (value.paymentMethod === CheckoutPaymentMethod.HeyLight) {
      if (!value.dialCode) {
        return this.createError({
          path: "dialCode",
          message: "Codice paese richiesto",
        });
      }
      if (!value.countryCode) {
        return this.createError({
          path: "countryCode",
          message: "Codice paese richiesto",
        });
      }
      if (!value.number) {
        return this.createError({
          path: "number",
          message: "Numero di telefono richiesto",
        });
      }
      if (!/^\d{6,15}$/.test(value.number)) {
        return this.createError({
          path: "number",
          message: "Numero di telefono non valido",
        });
      }
    }
    return true;
  })
  .test(
    "has-at-least-sdi-or-pec",
    "Inserisci almeno un codice SDI o PEC",
    (value, { createError }) => {
      if (!value.invoiceRequired) return true;
      if ([BusinessType.Person].includes(value.businessType as BusinessType))
        return true;
      const isValid = !!value.sdiCode || !!value.pec;
      if (!isValid) {
        const error = "Inserisci almeno un codice SDI o PEC";
        return createError({
          message: () => [
            new ValidationError(error, undefined, "sdiCode"),
            new ValidationError(error, undefined, "pec"),
          ],
        });
      }
      return isValid;
    },
  );
